import React, { useState, useRef } from 'react';
import { Upload, FileText, Camera, Type, Loader2, CheckCircle, AlertCircle, X, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import ApiService from '../../services/api';

const MilitaryDocumentUpload = ({ userId, onDataExtracted, onClose }) => {
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'text'
  const [processing, setProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingResult, setProcessingResult] = useState(null);
  const [extractedBenefits, setExtractedBenefits] = useState([]);
  const [documentText, setDocumentText] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [processWithAI, setProcessWithAI] = useState(true);
  
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const supportedDocuments = [
    { type: 'LES', description: 'Leave and Earnings Statement', icon: '💰' },
    { type: 'Benefits Statement', description: 'TRICARE, GI Bill, SGLI statements', icon: '🏥' },
    { type: 'Military Orders', description: 'PCS, TDY, Deployment orders', icon: '📋' },
    { type: 'VA Letters', description: 'Benefits eligibility letters', icon: '🏛️' },
    { type: 'Medical Records', description: 'Military medical documents', icon: '⚕️' },
    { type: 'Awards & Decorations', description: 'Military awards documentation', icon: '🎖️' }
  ];

  const handleFileSelect = async (files, categoryType = null) => {
    if (files.length === 0) return;
    
    const file = files[0];
    await processFile(file, categoryType);
  };

  const processFile = async (file, categoryType = null) => {
    setProcessing(true);
    setUploadProgress(0);
    
    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', categoryType || 'auto');
      formData.append('process_with_ai', processWithAI);
      formData.append('category', selectedCategory?.type || 'general');

      // Upload and process
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/documents/upload`, {
        method: 'POST',
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        // Handle error responses
        const errorText = await response.text();
        let errorMessage = 'Upload failed';
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        setProcessingResult(result.processing_result);
        
        // Emit event to update document counter
        window.dispatchEvent(new CustomEvent('documentUploaded'));
        
        // Extract benefits if document was processed successfully with AI
        if (result.processing_result?.success && processWithAI) {
          await extractBenefitsFromResult(result.processing_result);
        }
      } else {
        throw new Error(result.error || 'Upload failed');
      }

    } catch (error) {
      console.error('File processing error:', error);
      
      // Parse error messages for user-friendly display
      let errorMessage = error.message;
      if (error.message.includes('File size') && error.message.includes('exceeds maximum')) {
        errorMessage = error.message + '\n\nTip: Try compressing your document or use a smaller file.';
      } else if (error.message.includes('too large for AI processing')) {
        errorMessage = 'Document is too large for AI processing. Please try a smaller document or split it into sections.';
      }
      
      setProcessingResult({
        success: false,
        error: errorMessage
      });
    } finally {
      setProcessing(false);
    }
  };

  const processTextInput = async (categoryType = null) => {
    if (!documentText.trim()) return;

    setProcessing(true);
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 15, 90));
      }, 300);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/analyze-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          user_id: userId,
          document_text: documentText,
          document_name: selectedCategory ? `${selectedCategory.type} - Manual Input` : 'Manual Text Input',
          document_type: categoryType || 'auto'
        })
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        // Handle error responses
        const errorText = await response.text();
        let errorMessage = 'Text processing failed';
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        setProcessingResult(result.processing_result);
        
        // Emit event to update document counter
        window.dispatchEvent(new CustomEvent('documentUploaded'));
        
        if (result.processing_result?.success) {
          await extractBenefitsFromResult(result.processing_result);
        }
      } else {
        throw new Error(result.error || 'Text processing failed');
      }

    } catch (error) {
      console.error('Text processing error:', error);
      
      // Parse error messages for user-friendly display
      let errorMessage = error.message;
      if (error.message.includes('too large for AI processing')) {
        errorMessage = 'Text is too large for AI processing. Please try a shorter document or split it into sections.';
      }
      
      setProcessingResult({
        success: false,
        error: errorMessage
      });
    } finally {
      setProcessing(false);
    }
  };

  const extractBenefitsFromResult = async (result) => {
    try {
      // Mock extraction for now - in real implementation this would call the API
      const mockBenefits = [];
      
      const extractedData = result.data?.extracted_data;
      if (extractedData?.benefits) {
        extractedData.benefits.forEach(benefit => {
          mockBenefits.push({
            name: benefit.name || 'Extracted Benefit',
            type: benefit.type || 'other',
            status: benefit.status || 'Active',
            coverage: benefit.coverage || '',
            source: 'AI Document Processing',
            confidence: result.confidence || 'medium'
          });
        });
      }

      setExtractedBenefits(mockBenefits);
    } catch (error) {
      console.error('Benefit extraction error:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files, selectedCategory?.type);
  };

  const handleCategoryClick = (document) => {
    setSelectedCategory(document);
    // Reset previous results
    setProcessingResult(null);
    setExtractedBenefits([]);
    setDocumentText('');
    setUploadProgress(0);
  };

  const handleManualUpload = (categoryType) => {
    setSelectedCategory({ type: categoryType });
    // Trigger file input for this category
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const confirmExtractedData = () => {
    if (onDataExtracted) {
      onDataExtracted({
        benefits: extractedBenefits,
        documentInfo: processingResult,
        source: 'AI Document Processing',
        category: selectedCategory?.type || 'Unknown'
      });
    }
    onClose();
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="border-0 bg-white/95 backdrop-blur-sm max-w-4xl mx-auto">
      <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Upload className="w-6 h-6" />
            <div>
              <CardTitle className="text-xl">Military Document AI Processing</CardTitle>
              <p className="text-green-100 text-sm">Upload documents and let AI extract your military data</p>
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        {!processingResult ? (
          <>
            {/* Processing Mode Selection */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Processing Options</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="ai-processing"
                    name="processing-mode"
                    checked={processWithAI}
                    onChange={() => setProcessWithAI(true)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="ai-processing" className="flex-1 cursor-pointer">
                    <div className="font-medium text-blue-900">AI Processing & Storage</div>
                    <div className="text-sm text-blue-700">
                      Extract military benefits and data using AI, then store both original and processed results
                    </div>
                  </label>
                </div>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="storage-only"
                    name="processing-mode"
                    checked={!processWithAI}
                    onChange={() => setProcessWithAI(false)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="storage-only" className="flex-1 cursor-pointer">
                    <div className="font-medium text-blue-900">Storage Only</div>
                    <div className="text-sm text-blue-700">
                      Store document securely for future access without AI processing
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Supported Documents */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Supported Military Documents</h3>
              <p className="text-sm text-gray-600 mb-4">Click on a category to upload documents specifically for that type:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {supportedDocuments.map((doc) => (
                  <button
                    key={doc.type}
                    onClick={() => handleCategoryClick(doc)}
                    className={`bg-gray-50 p-3 rounded-lg border transition-all duration-200 hover:bg-blue-50 hover:border-blue-300 hover:shadow-md text-left ${
                      selectedCategory?.type === doc.type ? 'bg-blue-50 border-blue-400 shadow-md' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{doc.icon}</span>
                      <span className="font-medium text-sm text-gray-900">{doc.type}</span>
                    </div>
                    <p className="text-xs text-gray-600">{doc.description}</p>
                    {selectedCategory?.type === doc.type && (
                      <div className="mt-2 text-xs text-blue-600">
                        ✓ Selected for upload
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              {selectedCategory && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{selectedCategory.icon}</span>
                    <span className="font-medium text-blue-900">Selected: {selectedCategory.type}</span>
                  </div>
                  <p className="text-sm text-blue-700 mb-3">{selectedCategory.description}</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleManualUpload(selectedCategory.type)}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload {selectedCategory.type}
                    </Button>
                    <Button
                      onClick={() => setUploadMode('text')}
                      size="sm"
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      <Type className="w-4 h-4 mr-2" />
                      Paste Text
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Upload Modes */}
            <Tabs value={uploadMode} onValueChange={setUploadMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="file" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Upload File
                </TabsTrigger>
                <TabsTrigger value="text" className="flex items-center gap-2">
                  <Type className="w-4 h-4" />
                  Paste Text
                </TabsTrigger>
              </TabsList>

              <TabsContent value="file" className="mt-6">
                {/* File Upload */}
                <div
                  ref={dropZoneRef}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragOver ? 'border-green-400 bg-green-50' : 'border-gray-300'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={(e) => handleFileSelect(Array.from(e.target.files), selectedCategory?.type)}
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.txt,.doc,.docx"
                    className="hidden"
                  />
                  
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    {selectedCategory ? `Upload ${selectedCategory.type}` : 'Drop files here or click to upload'}
                  </h4>
                  <p className="text-gray-600 mb-4">
                    {selectedCategory ? 
                      `Selected category: ${selectedCategory.type}` : 
                      'Supports PDF (max 5MB), images (max 2MB), text and Word docs (max 3MB)'
                    }
                  </p>
                  
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={processing}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {selectedCategory ? `Select ${selectedCategory.type}` : 'Select Files'}
                    </Button>
                    
                    {selectedCategory && (
                      <Button
                        onClick={() => setSelectedCategory(null)}
                        variant="outline"
                        disabled={processing}
                      >
                        Clear Selection
                      </Button>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="text" className="mt-6">
                {/* Text Input */}
                <div className="space-y-4">
                  {selectedCategory && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{selectedCategory.icon}</span>
                        <span className="font-medium text-blue-900">Pasting {selectedCategory.type} Text</span>
                      </div>
                      <p className="text-sm text-blue-700 mt-1">{selectedCategory.description}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {selectedCategory ? `Paste ${selectedCategory.type} Document Text` : 'Paste Document Text'}
                    </label>
                    <textarea
                      value={documentText}
                      onChange={(e) => setDocumentText(e.target.value)}
                      placeholder={
                        selectedCategory ? 
                          `Paste your ${selectedCategory.type} document text here...` :
                          "Paste your military document text here (LES, benefits statement, orders, etc.)..."
                      }
                      rows={12}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-mono"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={() => processTextInput(selectedCategory?.type)}
                      disabled={processing || !documentText.trim()}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Type className="w-4 h-4 mr-2" />
                      {selectedCategory ? `Process ${selectedCategory.type} Text` : 'Process Text with AI'}
                    </Button>
                    
                    {selectedCategory && (
                      <Button
                        onClick={() => setSelectedCategory(null)}
                        variant="outline"
                        disabled={processing}
                      >
                        Clear Selection
                      </Button>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Processing Progress */}
            {processing && (
              <div className="mt-6 p-6 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  <span className="font-medium text-blue-900">Processing Document with AI...</span>
                </div>
                <Progress value={uploadProgress} className="h-2 mb-2" />
                <p className="text-sm text-blue-700">
                  {uploadProgress < 50 ? 'Uploading document...' : 
                   uploadProgress < 90 ? 'Analyzing with Claude AI...' : 
                   'Extracting military data...'}
                </p>
              </div>
            )}
          </>
        ) : (
          /* Processing Results */
          <div className="space-y-6">
            {processingResult.success ? (
              <>
                {/* Success Results */}
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-green-900">Document Processed Successfully!</h3>
                    <p className="text-sm text-green-700">
                      AI has analyzed your document and extracted military data
                    </p>
                  </div>
                </div>

                {/* Document Info */}
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">Document Analysis</h4>
                    <div className="flex gap-2">
                      {selectedCategory && (
                        <Badge className="bg-blue-100 text-blue-800">
                          {selectedCategory.type}
                        </Badge>
                      )}
                      <Badge className={getConfidenceColor(processingResult.confidence)}>
                        {processingResult.confidence} confidence
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    <strong>Type:</strong> {processingResult.data?.document_type || 'Unknown'}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Processed:</strong> {new Date(processingResult.processed_at).toLocaleString()}
                  </p>
                  {selectedCategory && (
                    <p className="text-sm text-gray-600">
                      <strong>Category:</strong> {selectedCategory.type}
                    </p>
                  )}
                </div>

                {/* Extracted Benefits */}
                {extractedBenefits.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900">Extracted Benefits ({extractedBenefits.length})</h4>
                    {extractedBenefits.map((benefit) => (
                      <div key={benefit.name || benefit.type} className="bg-white p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-gray-900">{benefit.name}</h5>
                          <Badge className="bg-blue-100 text-blue-800 capitalize">
                            {benefit.type}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Status:</span>
                            <span className="ml-2 text-gray-900">{benefit.status}</span>
                          </div>
                          {benefit.coverage && (
                            <div>
                              <span className="text-gray-500">Coverage:</span>
                              <span className="ml-2 text-gray-900">{benefit.coverage}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={confirmExtractedData}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Add to My Benefits
                  </Button>
                  
                  <div className="flex gap-2">
                    {selectedCategory && (
                      <Button
                        onClick={() => {
                          setProcessingResult(null);
                          setExtractedBenefits([]);
                          setDocumentText('');
                          setUploadProgress(0);
                          // Keep the same category selected
                        }}
                        variant="outline"
                        size="sm"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Another {selectedCategory.type}
                      </Button>
                    )}
                    
                    <Button
                      onClick={() => {
                        setProcessingResult(null);
                        setExtractedBenefits([]);
                        setDocumentText('');
                        setUploadProgress(0);
                        setSelectedCategory(null);
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Process Another
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* Error Results */
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  <div>
                    <h3 className="font-semibold text-red-900">Processing Failed</h3>
                    <p className="text-sm text-red-700">{processingResult.error}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setProcessingResult(null);
                      setExtractedBenefits([]);
                      setDocumentText('');
                      setUploadProgress(0);
                      // Keep category selection for retry
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Try Again
                  </Button>
                  
                  <Button
                    onClick={() => {
                      setProcessingResult(null);
                      setExtractedBenefits([]);
                      setDocumentText('');
                      setUploadProgress(0);
                      setSelectedCategory(null);
                    }}
                    variant="outline"
                    size="sm"
                  >
                    Reset
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MilitaryDocumentUpload;